import React, { useEffect, useState } from 'react';
import { fmtDate } from '../constants';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Signature / License / Date tri-column footer used at the very end of the report
// (either on the main page when Tympanometry is inline, or on the separate page when deferred).
//
// Auto-fetches the signing audiologist's drawn signature (if any) and renders
// it above the line. Falls back gracefully to the typed name if no sig is on
// file. The license number from the user's profile takes precedence over any
// `license` prop passed in (so reports don't need to know the audiologist's id).
export const SignatureFooter = ({ audiologistName, audiologistUserId, license }) => {
  const [sigUrl, setSigUrl] = useState(null);
  const [resolvedLicense, setResolvedLicense] = useState(license || '');

  useEffect(() => {
    let alive = true;
    let blobUrl = null;
    if (!audiologistUserId) return;
    (async () => {
      try {
        // Use fetch (not axios) so a 404 doesn't trip our global axios
        // interceptor — that interceptor reads `responseText`, which the
        // browser blocks when responseType is 'blob', producing a noisy
        // dev-mode overlay even though the page itself is fine.
        //
        // 2026-02 · `credentials: 'include'` is REQUIRED — the app's
        // primary auth is now httpOnly session cookies (see
        // AuthContext.js P1 XSS hardening). `credentials: 'omit'`
        // strips the cookie and the backend returns 401, so the
        // signature img never renders and the License No. column
        // reads `—` — even though the audiologist_user_id prop is
        // correct. The legacy Bearer fallback still ships for users
        // on the pre-cookie build (their localStorage.acs.token
        // survives), but new logins are cookie-only.
        const url = `${API}/settings/users/${audiologistUserId}/signature`;
        const headers = {};
        try {
          const tok = localStorage.getItem('acs.token');
          if (tok) headers.Authorization = `Bearer ${tok}`;
        } catch { /* ignore */ }
        const res = await fetch(url, { headers, credentials: 'include' });
        if (!alive) return;
        if (!res.ok) {
          // Surface auth/permission problems in the console so a
          // future regression (e.g. cookie migration take-3) is
          // debuggable without a full re-run of the testing agent.
          if (res.status !== 404) {
            // eslint-disable-next-line no-console
            console.warn(
              `[SignatureFooter] signature fetch failed: ${res.status} for user ${audiologistUserId}`,
            );
          }
          return;
        }
        const blob = await res.blob();
        blobUrl = URL.createObjectURL(blob);
        setSigUrl(blobUrl);
        const lic = res.headers.get('x-license-no');
        if (lic && !license) setResolvedLicense(lic);
      } catch {
        // No signature on file — typed name fallback is fine.
      }
    })();
    return () => {
      alive = false;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [audiologistUserId, license]);

  return (
    <footer className="mt-1.5 pt-1 border-t border-gray-400 grid grid-cols-3 gap-3 text-[11px]" data-testid="report-signature-footer">
      {/* Tighter three-column footer — reduced top/bottom rhythm so the
          block sits compactly at the bottom of the report without wasting
          vertical space. Inner content row shrunk from h-12 → h-8 so the
          signature image, license value, and date still share one baseline
          across columns. */}
      <div>
        <div className="text-gray-500 mb-0.5">Signature</div>
        <div className="h-8 flex items-end">
          {sigUrl ? (
            <img
              src={sigUrl}
              alt="Audiologist signature"
              className="max-h-8 max-w-full object-contain"
              data-testid="report-signature-img"
            />
          ) : null}
        </div>
        <div className="border-b border-gray-400"></div>
        <div className="mt-0.5 font-semibold">{audiologistName || '—'}</div>
      </div>
      <div>
        <div className="text-gray-500 mb-0.5">License No.</div>
        <div className="h-8"></div>
        <div className="border-b border-gray-400"></div>
        <div className="mt-0.5 font-semibold">{resolvedLicense || '—'}</div>
      </div>
      <div>
        <div className="text-gray-500 mb-0.5">Date</div>
        <div className="h-8"></div>
        <div className="border-b border-gray-400"></div>
        <div className="mt-0.5 font-semibold">{fmtDate()}</div>
      </div>
    </footer>
  );
};
